import React, { useContext, useEffect, useCallback, useState } from 'react';
import useReactRouter from 'use-react-router';

import Redirect from 'components/Redirect';
import useQueryParameters from 'hooks/useQueryParameters';
import useWorkflow from 'hooks/useWorkflow';
interface EntryToken {
  entryToken: string | null;
  createToken: () => void;
  clearToken: () => void;
}

const EntryTokenContext = React.createContext<EntryToken | null>(null);

// Ported from previous app
// TODO: figure out if this is necessary. Looks like a boolean flag may be
// sufficient.
const generateToken = (): string =>
  Math.random()
    .toString(36)
    .substring(7);

/**
 * Used to share the entry token across the app.
 * EntryRoute uses this to create token, and NonEntryRoute redirects to
 * root if the token does not exist. NoReturnRoute clears token if
 * token exists otherwise redirects to root
 */
const EntryTokenProvider: React.FC = ({ children }) => {
  const [entryToken, setEntryToken] = useState<string | null>(null);

  const createToken = useCallback(() => {
    if (entryToken === null) {
      setEntryToken(generateToken());
    }
  }, [entryToken, setEntryToken]);

  const clearToken = useCallback(() => {
    setEntryToken(null);
  }, [setEntryToken]);

  const context: EntryToken = {
    entryToken,
    createToken,
    clearToken,
  };
  return (
    <EntryTokenContext.Provider value={context}>
      {children}
    </EntryTokenContext.Provider>
  );
};

const EntryRoute: React.FC = ({ children }) => {
  const context = useContext(EntryTokenContext);
  useEffect(() => {
    if (context && !context.entryToken) {
      context.createToken();
    }
  }, [context]);
  return <>{children}</>;
};

const NonEntryRoute: React.FC = ({ children }) => {
  const { steps, pathFor } = useWorkflow();
  const {
    location: { search },
  } = useReactRouter();
  const context = useContext(EntryTokenContext);
  const { redirectToRoot } = useQueryParameters();

  useEffect(() => {
    // If enter via redirectToRoot, consider this an entry route (so that
    // NoReturnRoute works later)
    if (!redirectToRoot && context) {
      context.createToken();
    }
  }, [context, redirectToRoot]);

  if (!redirectToRoot || (context && context.entryToken)) {
    return <>{children}</>;
  }
  // Ensure we preserve all query params on redirect.
  const to = {
    pathname: pathFor(steps[0]),
    search: search,
  };
  return <Redirect to={to} />;
};

const NoReturnRoute: React.FC = ({ children }) => {
  const { steps, pathFor } = useWorkflow();
  const context = useContext(EntryTokenContext);
  // Keep track of whether we have the entry token initially.
  // We can't count on the token from the context because once we clear
  // it in the effect, the component will re-render and immediately redirect.
  const [initialToken] = useState(context && context.entryToken);
  const {
    location: { search },
  } = useReactRouter();

  useEffect(() => {
    if (context) {
      context.clearToken();
    }
  }, [context]);

  if (!initialToken) {
    const to = {
      pathname: pathFor(steps[0]),
      search: search,
    };

    return <Redirect to={to} />;
  }

  return <>{children}</>;
};
export { EntryTokenProvider, EntryRoute, NonEntryRoute, NoReturnRoute };
